import { cn } from "@/libs/utils";
import ContentsService from "@/services/ContentsService";
import { BhapticsContent } from "@/store/reducers/contents";
import PlayCircleIcon from "@/v2-components/core/icon/PlayCircleIcon";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { Badge } from "@/v2-components/ui/badge";
import { buttonVariants } from "@/v2-components/ui/button";
import Link from "next/link";
import ExternalLinkIcon from "@/v2-components/core/icon/external-link-icon";
import OverwolfIcon from "@/v2-components/core/icon/overwolf-icon";
import { useTrans } from "@/utils/useTrans";
import GameKeySeller from "@/components/compatibility/GameKeySeller";
import { GameResell } from "@/models/GameResell";
import { useGameResellKeyCounts } from "@/hooks/useGameResell";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import ArrowLeftIcon from "@/v2-components/core/icon/arrow-left-icon";
import { useRouter } from "next/router";
import { useCurrency } from "@/hooks/useCurrency";
import KeyIcon from "@/assets/icons/KeyIcon";
import NextImage from "@/components/shared/NextImage";
import { GameUtil } from "@/utils/GameUtil";
import { sellableKeyPlatforms } from "@/libs/cart/cart-game-stock";
import { SupportTypeUtil } from "@/utils/SupportTypeUtil";
import { getGuideHref } from "@/libs/games/game-guide";
import { isBundleOnlyGameResell } from "@/libs/games/bundle-only-game-key";

type Media = {
  url: string;
  type: "video" | "image";
};

interface Props {
  game: BhapticsContent;
}
export default function GameMetadataSection({ game }: Props) {
  const router = useRouter();
  const [selectedMedia, setSelectedMedia] = useState<Media>({
    url: game.videoLink ? game.videoLink : game.imageLinks[0],
    type: game.videoLink ? "video" : "image",
  });
  const isOverwolf = ContentsService.overwolfSupported(game.supportedPlatforms);
  const isMobileView = useIsMobileView();

  return (
    <section>
      {/**
       * 시안 — 모바일 `30035:18963` **360×360** · 데스크탑 `29889:101367` **640**.
       *
       * 종전 모바일 값은 `490` 이었다(시안 근거 없음). 데스크탑만 맞았다.
       *
       * ★**줄여도 오버레이가 안 넘친다** — 카피가 하단 정렬이라 상자와 함께 올라온다.
       * 실측(360px · `mj-test`):
       *
       * ```
       *   490  자식 14 · 넘침 0 · 최하단 400   (여유 90)
       *   360  자식 14 · 넘침 0 · 최하단 270   (여유 90)
       * ```
       *
       * ⚠️ 긴 라벨 로케일에서 위쪽으로 밀리는 방향이 위험이다 — fr 로 재확인했다.
       */}
      <div className="relative h-[360px] w-full bg-black laptop:h-[640px]">
        <FullscreenMedia selectedMedia={selectedMedia} />
        <GradientOverlayFromEdges />
        <button
          className="absolute left-4 top-5 z-10 text-white laptop:hidden"
          onClick={() => router.back()}
        >
          <ArrowLeftIcon />
        </button>

        <div className="absolute inset-0 mx-4 flex items-end justify-center text-white">
          <div className="flex w-full max-w-screen-max justify-between">
            <div className="flex w-full flex-col">
              <GameMetadataInfo game={game} />
              <MediaSelect
                game={game}
                selectedMedia={selectedMedia}
                setSelectedMedia={setSelectedMedia}
              />
            </div>
            {game.gameResell && !isMobileView && (
              <DesktopGameKeySeller gameResell={game.gameResell} />
            )}
          </div>
        </div>
      </div>
      {isOverwolf && <OverwolfBanner />}
      {/**
       * ★모바일 순서: 히어로 → (Overwolf 배너) → **게임 키** → 가이드 버튼.
       *
       * 시안 모바일은 변형이 둘인데 **서로를 안 보여준다**:
       *   `30035:18956`(키 있는 변형)  gd-hero → `Frame 1382`(키셀러) → gd-effects …
       *   `30035:22454`(Overwolf 변형) gd-hero → Notice → `Btns` → gd-updates …
       * 즉 **둘 다 있는 게임의 순서는 시안에 없다.** 키셀러를 앞에 두는 것으로 확정했다
       * (구매 진입점이라 히어로에 붙인다).
       */}
      {game.gameResell && isMobileView && (
        <MobileGameKeySeller gameResell={game.gameResell} />
      )}
      {/* 게이트를 `Mod` → **가이드 유무**로 넓혔다(#5474). 근거·수치는
          `GameGuideButtons` 주석 참조 — 데스크탑(aside)과 같은 규칙이어야
          폭에 따라 버튼이 나타났다 사라지지 않는다. */}
      <MobileMetadataButtons game={game} />
    </section>
  );
}

function FullscreenMedia({ selectedMedia }: { selectedMedia: Media }) {
  /**
   * desktop safari에서 webm 영상이 자동재생되지 않는 문제를 해결하기 위함
   */
  const [shouldPlay, setShouldPlay] = useState(false);
  useEffect(() => {
    setShouldPlay(true);
  }, []);

  return (
    <>
      {selectedMedia.type === "video" ? (
        <video
          className="absolute h-full w-full bg-black object-cover"
          controls={false}
          loop
          muted
          autoPlay
          playsInline
        >
          {shouldPlay && <source src={selectedMedia.url} />}
        </video>
      ) : (
        <Image
          src={selectedMedia.url}
          className="absolute h-full w-full object-cover"
          alt="game image"
          sizes="100%"
          fill
        />
      )}
    </>
  );
}

function GradientOverlayFromEdges() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_50%,#000000_100%),linear-gradient(0deg,rgba(0,0,0,0)_50%,#000000_100%)] laptop:bg-[linear-gradient(180deg,rgba(0,0,0,0)_50%,#000000_100%),linear-gradient(90deg,rgba(0,0,0,0)_50%,#000000_100%),linear-gradient(270deg,rgba(0,0,0,0)_50%,#000000_100%)]" />
  );
}

function OverwolfBanner() {
  const { t } = useTrans(["games"]);
  return (
    <div className="flex w-full justify-center bg-coolgray-80 p-4 text-white">
      <div className="flex w-full max-w-screen-max justify-between gap-3">
        <OverwolfIcon className="h-8 w-8 shrink-0" />
        <div className="flex flex-1 flex-col gap-2 laptop:flex-row laptop:items-center laptop:justify-between">
          <p className="text-sm laptop:text-base">
            {t("This PC game mod is an official mod supported by Overwolf.")}
          </p>
          <Link
            href="http://overwolf.com/"
            target="_blank"
            className="text-sm underline laptop:text-base"
          >
            {t("Learn about Overwolf")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function GameMetadataInfo({ game }: { game: BhapticsContent }) {
  const badgeVariant = SupportTypeUtil.getBadgeVariant(game.supportedPlatforms);
  const badgeText = SupportTypeUtil.getBadgeText(game.supportedPlatforms);
  const unavailablePlatforms = GameUtil.getTempUnavailablePlatforms(
    game.supportedPlatforms,
  );

  return (
    <>
      <div className="mb-2.5 flex items-center gap-2 laptop:mb-3">
        <Badge size="sm" variant={badgeVariant}>
          {badgeText}
        </Badge>
        {/* 번들 전용 게임 키(#6053)는 단독으로 안 판다 — 「GAME KEY」 배지를 달지 않는다 */}
        {game.gameResell && !isBundleOnlyGameResell(game.gameResell) && (
          <Badge size="sm" variant="gamekey" className="flex gap-1">
            <KeyIcon />
            <span>GAME KEY</span>
          </Badge>
        )}
        {game.badges.map((badge) => (
          <Badge
            key={badge}
            size="sm"
            variant={badge.toLocaleLowerCase() as "new" | "best"}
          >
            {badge}
          </Badge>
        ))}
      </div>
      <h1 className="mb-2.5 text-4xl laptop:mb-3 laptop:text-7xl">
        {game.name}
      </h1>
      {/**
       * ★**히어로에는 서브카테고리만 남긴다** (#5679 · 디자이너 지정 2026-09-09).
       *
       * 종전엔 이 줄에 별점·평균·리뷰수(`★★★★☆ 3.7 (5) •`)가, 아래 줄에
       * 「Platform │ Device │ Effect」 묶음이 있었다. 셋 다 걷었다.
       *
       * 근거가 셋이고 서로 맞는다:
       *
       * ```
       *   시안 `30035:21353`   배지 · 제목 · 서브카테고리 · 썸네일 — 평점도 아이콘 줄도 없다
       *   지정 이미지 A         Platform/Device 줄에 동그라미 (평점 없는 게임)
       *   지정 이미지 B         평점 줄 + Platform/Device/Effect 줄에 동그라미
       * ```
       *
       * `subCategory` 는 이미지 A 의 `Horror` 가 동그라미 **밖**이고 시안에도 `Social` 이
       * 남아 있어 유지다.
       *
       * ## ⚠️ 정보도 진입점도 잃지 않는다 — 실측으로 확인했다
       *
       * ```
       *   지원 플랫폼 · 지원 기기   aside 「게임 정보」 카드(`GameInfo.tsx` 의 `Supported Platform`·`Supported Device`)
       *                            + 하단 `SupportedDevices` 섹션 · 시안 `29889:101935` 에도 있다
       *   이펙트 수                본문 `HapticEffects` 섹션
       *   평점                     본문 `HapticFeedbackReview`(게이지 3 + 추천율)
       * ```
       *
       * 별점은 **리뷰 다이얼로그를 여는 버튼**이기도 했다(`reviewCount > 3`). 그 진입점은
       * `HapticFeedbackReview` 가 `=== 0` 과 `> 0` **두 분기 모두**에서 같은
       * `view="rate"` 다이얼로그를 띄우므로 남는다 — `> 3` 은 그 부분집합이다.
       * 실측(1440 · dev): `half-life-alyx`(리뷰 5건 · 히어로 별점 있던 게임)에서
       * 「햅틱 평가」의 `Rate My Experience` 버튼이 함께 떠 있었다.
       */}
      <div className="flex items-center gap-1 text-sm capitalize laptop:text-lg">
        {game.subCategory}
      </div>
      {/* ★데스크탑 가이드 버튼은 **aside 로 옮겼다**(#5474, 시안 `30035:23140`).
          `GameGuideButtons` 가 「게임 정보」 카드 위에서 세로 2개로 그린다.
          모바일은 시안과 이미 같아서(세로 풀폭) 히어로에 남긴다 — 아래 참조. */}
      {!!unavailablePlatforms.length && (
        <Unavailable
          className="mt-2 laptop:mt-3"
          unavailablePlatforms={unavailablePlatforms}
        />
      )}
    </>
  );
}

function Unavailable({
  unavailablePlatforms,
  className,
}: {
  className?: string;
  unavailablePlatforms: string[];
}) {
  const { t } = useTrans(["games"]);
  const platforms = unavailablePlatforms.join(", ");
  return (
    <div className={cn("text-sm text-coolgray-60 laptop:text-lg", className)}>
      <p className="overflow-hidden text-ellipsis text-nowrap text-red-60">
        {"!"}{" "}
        {t("Temporarily unavailable on {{platforms}}", {
          platforms,
          defaultValue: `Temporarily unavailable on ${platforms}`,
        })}
      </p>
    </div>
  );
}

function MobileMetadataButtons({ game }: { game: BhapticsContent }) {
  const { t } = useTrans(["games"]);
  const isOverwolf = ContentsService.overwolfSupported(game.supportedPlatforms);
  const guideHref = getGuideHref(game);

  if (!guideHref && !isOverwolf) return null;

  return (
    /**
     * ⚠️ **구분선(`border-b-8`)을 두지 않는다** — 시안 `30035:22454` 의 `Btns` 는 자식이
     * 버튼 2개뿐이고, 아래 `gd-updates` 와는 **갭 48 로만** 분리된다. 키 블록
     * (`30035:18956` `Frame 1382`)에는 `Divider 360×8` 이 있어서 서로 다르다.
     *
     * 둘 다 있는 게임이면 구분선이 키 블록 끝에만 남는다 — 그 조합은 시안에 없고,
     * 「구매 블록 ↔ 가이드 블록」을 가르는 자리라 그대로 둔다.
     */
    <div className="flex flex-col gap-2 px-4 py-5 laptop:hidden">
      {guideHref && (
        <Link
          href={guideHref}
          target="_blank"
          className={cn(
            buttonVariants({
              variant: "blue-fill",
              shape: "pill",
              size: "sm",
            }),
            "px-8",
          )}
        >
          {t("How to Play")}
        </Link>
      )}
      {isOverwolf && (
        <Link
          href="https://www.overwolf.com/app/bhaptics_inc.-bhaptics_link"
          target="_blank"
          className={cn(
            buttonVariants({
              variant: "darkgray-fill",
              shape: "pill",
              size: "sm",
            }),
            "px-8",
          )}
        >
          <span>{t("Install Overwolf Plugin")}</span>
          <ExternalLinkIcon className="h-5 w-5" />
        </Link>
      )}
    </div>
  );
}

interface MediaSelectProps {
  game: BhapticsContent;
  selectedMedia: Media;
  setSelectedMedia: (media: Media) => void;
}
function MediaSelect({
  game,
  selectedMedia,
  setSelectedMedia,
}: MediaSelectProps) {
  /**
   * ★**위 패딩은 두지 않는다 — 서브카테고리와의 간격은 그쪽 `mb` 가 준다** (#5679).
   *
   * 종전 `py-5 laptop:py-16` 은 **양쪽**에 20/64 를 걸었다. 위 여백이 서브카테고리의
   * `mb` 에 **더해져** 보이는 간격이 시안의 3배가 됐다:
   *
   * ```
   *            박스간(mb)   레일 pt   보이는 간격   시안
   *   1440        32         64          96         32
   *    390        10         20          30         10
   * ```
   *
   * ⚠️ 처음엔 박스간 간격만 재고 「맞다」고 적었다 — **패딩 안쪽에서 콘텐츠가 시작하는
   * 것을 안 봤다.** 간격은 요소 사각형이 아니라 **첫 자식**까지 재야 한다.
   *
   * 시안(`29889:102096`)은 `Left` 가 `flex-col gap-[32px]` 이고 `Bottom`(썸네일 묶음)에
   * **패딩이 없다** — 그래서 32 가 곧 보이는 간격이다.
   *
   * 아래 패딩은 남긴다(`pb-5 laptop:pb-16`). 시안에서 `Left` 가 y=354·h=222 로 끝나
   * 640 짜리 히어로 바닥까지 **64** 가 남는다.
   *
   * `-mx-4 px-4` 는 가로 스크롤이 화면 가장자리까지 흐르게 하는 것이라 그대로 둔다.
   */
  return (
    <div className="scrollbar-none -mx-4 flex items-center gap-3 overflow-x-auto px-4 pb-5 pt-2.5 laptop:gap-6 laptop:pb-16 laptop:pt-8">
      {game.videoLink && (
        <div
          onClick={() =>
            setSelectedMedia({ type: "video", url: game.videoLink })
          }
          className={cn(
            "relative flex aspect-[128/72] h-[50px] shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded laptop:h-18 laptop:rounded-lg",
            selectedMedia.url === game.videoLink &&
              "outline outline-2 outline-blue-30",
          )}
        >
          <Image
            src={game.thumbnailWideLink}
            alt={"game thumbnail image"}
            className="absolute object-cover"
            sizes="128px"
            fill
          />
          <PlayCircleIcon className="absolute h-9 w-9 text-black invert" />
        </div>
      )}
      {game.imageLinks.map((image, index) => {
        return (
          <div
            key={`${image}-${index}`}
            onClick={() => setSelectedMedia({ type: "image", url: image })}
            className={cn(
              "relative flex aspect-[128/72] h-[50px] shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded laptop:h-18 laptop:rounded-lg",
              selectedMedia.url === image &&
                "outline outline-2 outline-blue-30",
            )}
          >
            <NextImage
              src={image}
              alt={"game thumbnail image"}
              className="absolute object-cover"
              sizes="128px"
              fill
            />
          </div>
        );
      })}
    </div>
  );
}

function DesktopGameKeySeller({ gameResell }: { gameResell: GameResell }) {
  const currency = useCurrency();
  const gamekeyCounts = useGameResellKeyCounts(gameResell.id);

  // 팔 플랫폼이 없으면 히어로를 숨긴다 — 재고 확인 중 · 모름 · 품절 전부. `GameKeySeller` 안의 목록과 **같은
  // 판정**이라, 히어로가 떴는데 고를 플랫폼이 없는 상태가 없다(종전 `every(pending === 0)` 은 가격 없는 플랫폼의
  // 재고까지 셌다).
  if (
    sellableKeyPlatforms(gameResell.platformList, gamekeyCounts.data, currency)
      .length === 0
  ) {
    return null;
  }

  /**
   * 시안 `29889:101386` — `w-400 · p-16 · r16 · bg white/20 · backdrop-blur-**24**`.
   * ★블러가 48 이었다(실측). 시안은 24 다.
   */
  return (
    <div className="mb-16 hidden min-w-[400px] self-end rounded-2xl bg-white/20 p-4 text-white backdrop-blur-[24px] laptop:block">
      <GameKeySeller
        keyCounts={gamekeyCounts.data}
        gameResell={gameResell}
        trigger="game_detail"
      >
        <GameKeySeller.RadioGroup variant="blur" showRadioIcon />
        <GameKeySeller.Button />
      </GameKeySeller>
    </div>
  );
}

function MobileGameKeySeller({ gameResell }: { gameResell: GameResell }) {
  const currency = useCurrency();
  const gamekeyCounts = useGameResellKeyCounts(gameResell.id);

  // 데스크톱과 같은 게이트 — `DesktopGameKeySeller` 주석.
  if (
    sellableKeyPlatforms(gameResell.platformList, gamekeyCounts.data, currency)
      .length === 0
  ) {
    return null;
  }

  /**
   * 시안 `30035:19674` — 블록 좌우 `px-[16px]`, 히어로와의 간격·버튼과의 간격이 **20**
   * (부모 `Frame 2147238911 gap-[20px]`). 종전 `p-4` 는 세로도 16 이었다.
   */
  return (
    <div className="w-full border-b-8 border-coolgray-10 bg-white px-4 py-5 laptop:hidden">
      <GameKeySeller
        keyCounts={gamekeyCounts.data}
        gameResell={gameResell}
        trigger="game_detail"
      >
        <GameKeySeller.RadioGroup />
        <GameKeySeller.Button />
      </GameKeySeller>
    </div>
  );
}
